import { useState, useEffect, useRef } from 'react';
import SynthControls from './components/SynthControls/SynthControls';
import FilterControls from './components/FilterControls/FilterControls';
import InputControls from './components/InputControls/InputControls';
import LimiterControl from './components/LimiterControl/LimiterControl';
import LamellaArea from './components/LamellaArea/LamellaArea';
import GlobalStrip from './components/GlobalStrip/GlobalStrip';
import styles from './App.module.css';
import { useSerialConnection } from './hooks/useSerialConnection';
import { useDeviceState } from './hooks/useDeviceState';

const LEFT_TABS = [
    { id: 'lamellas', label: 'Lamellas' },
    { id: 'synth', label: 'Synth' },
    { id: 'filter', label: 'Filter' },
];

const RIGHT_TABS = [
    { id: 'input', label: 'Input' },
    { id: 'limiter', label: 'Limiter' },
];

const ALL_TABS = [...LEFT_TABS, ...RIGHT_TABS];

export default function App() {
    const [visibleTabs, setVisibleTabs] = useState(
        ALL_TABS.reduce((acc, tab) => ({ ...acc, [tab.id]: true }), {})
    );

    // Device state management
    const {
        maxLoad,
        avgLoad,
        minLoad,
        deviceGain,
        envelope0,
        envelope1,
        masterOutLeft,
        masterOutRight,
        limiterGain,
        lastResponse,
        responseColor,
        processLine
    } = useDeviceState();

    // Serial connection management
    const {
        connected,
        status,
        portName,
        connect,
        disconnect,
        sendCommand
    } = useSerialConnection(processLine);

    const toggleTab = (tabId) => {
        setVisibleTabs(prev => ({ ...prev, [tabId]: !prev[tabId] }));
    };

    // Send default state to firmware on connect
    const hasInitialized = useRef(false);
    useEffect(() => {
        if (connected && !hasInitialized.current) {
            hasInitialized.current = true;
            // Small delay to ensure connection is stable
            setTimeout(() => {
                sendCommand('synth.source', 'WAVE');
                sendCommand('synth.waveform', 'SAW');
                sendCommand('synth.gain', '0.800');
                sendCommand('filter.type', 'SVF');
                sendCommand('filter.cutoff', '400.0');
                sendCommand('filter.res', '0.300');
            }, 100);
        }
        if (!connected) {
            hasInitialized.current = false;
        }
    }, [connected, sendCommand]);

    return (
        <div className={styles.container}>
            <GlobalStrip
                connected={connected}
                status={status}
                portName={portName}
                onConnect={connect}
                onDisconnect={disconnect}
                cpuMin={minLoad}
                cpuAvg={avgLoad}
                cpuMax={maxLoad}
                masterOutLeft={masterOutLeft}
                masterOutRight={masterOutRight}
            />
            {connected && <div className={styles.main}>
                {/* Left Panel */}
                <div className={styles.panel}>
                    <div className={styles.tabArea}>
                        {LEFT_TABS.map(tab => (
                            <button
                                key={tab.id}
                                className={`${styles.tab} ${visibleTabs[tab.id] ? styles.tabActive : ''}`}
                                onClick={() => toggleTab(tab.id)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                    <div className={styles.componentList}>
                        {visibleTabs.lamellas && (
                            <LamellaArea
                                envelope0={envelope0}
                                envelope1={envelope1}
                            />
                        )}
                        {visibleTabs.synth && <SynthControls sendCommand={sendCommand} />}
                        {visibleTabs.filter && <FilterControls sendCommand={sendCommand} />}
                    </div>
                </div>

                {/* Right Panel */}
                <div className={styles.panel}>
                    <div className={styles.tabArea}>
                        {RIGHT_TABS.map(tab => (
                            <button
                                key={tab.id}
                                className={`${styles.tab} ${visibleTabs[tab.id] ? styles.tabActive : ''}`}
                                onClick={() => toggleTab(tab.id)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                    <div className={styles.componentList}>
                        {visibleTabs.input && <InputControls sendCommand={sendCommand} />}
                        {visibleTabs.limiter && <LimiterControl sendCommand={sendCommand} limiterGain={limiterGain} />}
                    </div>
                </div>
            </div>}
        </div>
    );
}